<template>
  <div class="delivery__step">
    <MdsComboBox
      :label="translate('scheduler.modal.delivery.type.label')"
      :data-set="deliveryTypes"
      :selected-items="deliveryType"
      @input="updateDeliveryType($event)"
    />
    <component
      v-if="deliveryStepInfo.length"
      :is="component"
      :label="translate('scheduler.modal.delivery.destination.label')"
      :options="deliveryDestinations"
      :editedFeed="editedFeed"
      @input="updateDeliveryDestination"
      @disable-save-button="$emit('disable-save-button', $event)"
    />
    <attestationForm
      v-if="approvedForFeedDistribution"
      :editedFeed="editedFeed"
      @check-accept="handleAgreementCheck"
      @disable-save-button="$emit('disable-save-button', $event)"
    />
    <div class="delivery_step_separator">
      <hr />
    </div>
    <MdsInput
      v-model="name"
      required
      :error="!!error.length"
      :error-text="[error]"
      :label="translate('scheduler.modal.source.name.label')"
      :placeholder="translate('scheduler.modal.source.name.placeholder')"
      @input="$emit('name', $event)"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import MwcBaseHelpersMixin from '@morningstar/mcl-utilities/src/vue/mixins/mwc-base-mixin'
import MdsComboBox from '@mds/combo-box'
import MdsInput from '@mds/input'
import { compareCaseInsensitive } from '@/utils/compareStrings'
import { isNameInvalid } from '@/utils/nameValidation'
import defaultConfig from '@/components/schedulerModal/config/i18n'
import { DELIVERY_TYPES } from '@/components/schedulerModal/config/constants'
import emailField from '@/components/schedulerModal/schedulerSteps/deliveries/email.vue'
import destinationsField from '@/components/schedulerModal/schedulerSteps/deliveries/destinations.vue'
import attestationForm from '@/components/schedulerModal/schedulerSteps/helpers/attestationForm.vue'

export default {
  name: 'DeliveryStep',
  components: {
    MdsComboBox,
    MdsInput,
    emailField,
    destinationsField,
    attestationForm,
  },
  mixins: [MwcBaseHelpersMixin],
  props: {
    deliveryStepInfo: {
      type: Array,
      default: /* istanbul ignore next */ () => [],
    },
    withoutFeedName: Boolean,
  },
  data() {
    return {
      defaultConfig,
      deliveryType: [],
      name: '',
      error: [],
    }
  },
  mounted() {
    if (this.investmentItem && !this.editedFeed) {
      this.name = `${this.investmentItem}_Feed_${this.randomNumber}`
    }
  },
  watch: {
    name: {
      handler: 'validateFeedName',
    },
    withoutFeedName: {
      handler: 'validateFeedName',
    },
    editedFeed: {
      deep: true,
      immediate: true,
      handler(value) {
        if (value?.feedInfo?.name) {
          this.name = value?.feedInfo?.name
        }
      },
    },
    investmentItem: {
      deep: true,
      immediate: true,
      handler(value) {
        if (value && !this.editedFeed) {
          this.name = `${value}_Feed_${this.randomNumber}`
        }
      },
    },
    deliveryStepInfo: {
      immediate: true,
      deep: true,
      async handler(value) {
        if (!isEmpty(value)) {
          if (this.editedFeed && this.editedFeed.delivery_type) {
            this.deliveryType = [this.editedFeed.delivery_type]
          } else {
            this.deliveryType =
              compareCaseInsensitive(
                this.deliveryStepInfo[0].deliveryType,
                DELIVERY_TYPES.emails
              ) === 0
                ? [DELIVERY_TYPES.emails]
                : [DELIVERY_TYPES.ftps]
          }

          this.$emit('input', { delivery_type: this.deliveryType[0] })
        }
      },
    },
  },
  computed: {
    ...mapState({
      editedFeed: (state) => state.cache.cache.editedFeed,
      investmentItem: (state) => state.cache.cache.investmentItem,
      userSettings: (state) => state.cache.cache.userSettings,
    }),
    deliveryTypes() {
      return this.deliveryStepInfo.reduce((deliveryTypesList, parameter) => {
        const deliveryParam = {
          text:
            parameter.deliveryType === DELIVERY_TYPES.ftps.toLowerCase()
              ? parameter.deliveryType.toUpperCase()
              : parameter.deliveryType.replace(/^./, (str) =>
                  str.toUpperCase()
                ),
          value: parameter.deliveryType,
          routes: parameter.routes ?? [],
        }
        deliveryTypesList.push(deliveryParam)
        return deliveryTypesList
      }, [])
    },
    deliveryDestinations() {
      const { destinations = [] } = this.deliveryStepInfo.find(
        ({ deliveryType }) =>
          compareCaseInsensitive(deliveryType, this.deliveryType[0]) === 0
      )

      if (!destinations) {
        return []
      }

      if (
        compareCaseInsensitive(this.deliveryType[0], DELIVERY_TYPES.ftps) === 0
      ) {
        return destinations.map((route) => ({
          text:
            route.folder !== '/'
              ? `${route.server}/${route.folder}`
              : `${route.server}/`,
          value: route.id,
        }))
      }

      return destinations[0].addresses.map((email) => ({
        text: email,
      }))
    },
    component() {
      const [deliveryType] = this.deliveryType
      return compareCaseInsensitive(deliveryType, DELIVERY_TYPES.emails) === 0
        ? DELIVERY_TYPES.emailField
        : DELIVERY_TYPES.destinationsField
    },
    randomNumber() {
      return Math.floor(Math.random() * 1001)
    },
    approvedForFeedDistribution() {
      const { approved_for_feed_distribution = false } = this.userSettings
      return approved_for_feed_distribution
    },
  },
  methods: {
    handleAgreementCheck(newValue) {
      this.$emit('input', { attestation_form: newValue ? 'Yes' : 'No' })
      this.$emit('attestation-check', newValue)
    },
    updateDeliveryType(event) {
      this.deliveryType = event
      this.$emit('input', { delivery_type: event[0] })
      this.$emit('disable-save-button', false)
    },
    updateDeliveryDestination(event) {
      if (
        compareCaseInsensitive(this.deliveryType[0], DELIVERY_TYPES.ftps) === 0
      ) {
        if (event) {
          this.$emit('input', { delivery_destination: event })
        }
        this.$emit('disable-save-button', !event)
      }
      this.$emit('input', event)
    },
    async validateFeedName() {
      if (this.withoutFeedName) {
        const invalidName = await isNameInvalid(this.name)
        await this.$nextTick()

        const CONDITIONS = {
          [this.name.length > 200]: this.translate(
            'scheduler.modal.overflow.name.error'
          ),
          [this.name.length < 4]: this.translate(
            'scheduler.modal.underflow.name.error'
          ),
          [isEmpty(this.name)]: this.translate(
            'scheduler.modal.empty.name.error'
          ),
          [invalidName]: this.translate('scheduler.modal.invalid.name.error'),
        }

        this.error = CONDITIONS[true] || []

        if (!isEmpty(this.error)) {
          this.$emit('validName', false)
        } else {
          this.$emit('name', this.name)
          this.$emit('validName', true)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@mds/constants';
.delivery__step {
  min-height: 100px;
  height: 100px;
  margin-bottom: $mds-space-2-and-a-half-x;
  overflow-y: auto;
  width: 100%;
}

.delivery_step_separator {
  padding: $mds-space-1-x 0;
  hr {
    border: 1px dashed $mds-color-neutral-80;
    margin: $mds-space-2-and-a-half-x 0;
  }
}
</style>
